import { createFileRoute } from '@tanstack/react-router'
import { LEGAL_CONFIG, LEGAL_VERSIONS } from '@/legal.config'
import { LegalVersionMeta } from '@/routes/legal/-legal-version-meta'

export const Route = createFileRoute('/legal/_layout/privacy')({
  component: PrivacyPolicyPage,
})

function PrivacyPolicyPage() {
  return (
    <article className="prose prose-neutral dark:prose-invert max-w-none">
      <h1>Privacy Policy</h1>
      <LegalVersionMeta
        current={LEGAL_VERSIONS.privacy.current}
        history={LEGAL_VERSIONS.privacy.history}
      />

      <h2>1. Who we are</h2>
      <p>
        {LEGAL_CONFIG.companyName} (organisation number{' '}
        {LEGAL_CONFIG.companyOrgNumber}), {LEGAL_CONFIG.companyAddress}, is the
        data controller for personal data processed when you use{' '}
        {LEGAL_CONFIG.productName} (the "Service").
      </p>
      <p>
        Contact us about data protection at{' '}
        <a href={`mailto:${LEGAL_CONFIG.privacyEmail}`}>
          {LEGAL_CONFIG.privacyEmail}
        </a>
        .
      </p>

      <h2>2. Who this policy covers</h2>
      <p>This policy covers two categories of people:</p>
      <ul>
        <li>
          <strong>Candidates</strong> — individuals who complete an AI-assisted
          video, audio, or chat interview via the Service.
        </li>
        <li>
          <strong>Customers</strong> — organisations and their staff
          ("Recruiters") who use the Service to create jobs, review candidates,
          and make hiring decisions.
        </li>
      </ul>

      <h2>3. Data we collect</h2>

      <h3>3.1 Candidates</h3>
      <ul>
        <li>
          <strong>Identity &amp; contact:</strong> name and email address
          (provided by the recruiting organisation when creating your invite).
        </li>
        <li>
          <strong>Interview content:</strong> video recording, audio recording,
          and/or text chat transcript of your interview session.
        </li>
        <li>
          <strong>AI analysis:</strong> competency scores (1–5 scale) and
          written rationale generated by an AI model based solely on the content
          of your answers. The AI does not analyse tone of voice, facial
          expressions, hesitation, emotional state, or any delivery cue — only
          what you say.
        </li>
        <li>
          <strong>Technical data:</strong> IP address, browser type, and session
          metadata collected automatically when you access the interview link.
        </li>
      </ul>

      <h3>3.2 Recruiters / Customers</h3>
      <ul>
        <li>
          <strong>Account data:</strong> name, work email address, organisation
          name.
        </li>
        <li>
          <strong>Usage data:</strong> actions taken in the dashboard (jobs
          created, candidates reviewed, AI audit events).
        </li>
        <li>
          <strong>Billing data:</strong> payment method details are handled
          directly by our payment processor (Polar); we store only a customer
          identifier and subscription status.
        </li>
      </ul>

      <h2>4. Legal basis for processing</h2>
      <table>
        <thead>
          <tr>
            <th>Processing activity</th>
            <th>Legal basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Running the AI interview and storing the recording/transcript
            </td>
            <td>
              Legitimate interest of the recruiting organisation (our customer);
              candidate is informed and consents before starting.
            </td>
          </tr>
          <tr>
            <td>AI-generated competency scoring</td>
            <td>
              Legitimate interest; no automated decision is made — a human
              recruiter always reviews results (GDPR Art. 22 does not apply).
            </td>
          </tr>
          <tr>
            <td>Providing the Service to Customers</td>
            <td>Performance of a contract.</td>
          </tr>
          <tr>
            <td>Maintaining the AI audit log</td>
            <td>
              Legal obligation (EU AI Act Art. 12 — minimum 6-month retention
              for high-risk AI systems).
            </td>
          </tr>
          <tr>
            <td>Sending transactional emails</td>
            <td>Performance of a contract / legitimate interest.</td>
          </tr>
          <tr>
            <td>Error monitoring and analytics</td>
            <td>Legitimate interest (service reliability and improvement).</td>
          </tr>
        </tbody>
      </table>

      <h2>5. How we use your data</h2>
      <p>We use personal data solely to:</p>
      <ul>
        <li>Deliver the AI interview experience to candidates.</li>
        <li>
          Generate and present AI-assisted competency scores to recruiters.
        </li>
        <li>Allow recruiters to make informed, human-led hiring decisions.</li>
        <li>Maintain audit logs required by the EU AI Act.</li>
        <li>Send transactional emails (invite links, result explanations).</li>
        <li>Operate, secure, and improve the Service.</li>
      </ul>
      <p>
        We do not sell personal data. We do not use candidate data to train AI
        models.
      </p>

      <h2>6. AI-assisted decision making (EU AI Act)</h2>
      <p>
        The Service uses AI to generate competency scores and written rationale.
        This constitutes a high-risk AI system under EU AI Act Annex III,
        Category 4 (employment screening). The following safeguards are in
        place:
      </p>
      <ul>
        <li>
          A human recruiter reviews every AI-generated result. No hiring
          decision is made by AI alone.
        </li>
        <li>
          The AI evaluates only the content of answers — not tone, confidence,
          hesitation, emotional state, or any vocal or visual delivery cue.
        </li>
        <li>
          Every analysis run is logged with the exact prompt version and model
          used.
        </li>
        <li>
          Candidates may request a copy of their competency scores and rationale
          by emailing{' '}
          <a href={`mailto:${LEGAL_CONFIG.privacyEmail}`}>
            {LEGAL_CONFIG.privacyEmail}
          </a>
          .
        </li>
      </ul>

      <h2>7. Data processors (sub-processors)</h2>
      <p>
        To deliver the Service we share personal data with third-party
        sub-processors in the following categories: cloud infrastructure and
        database providers, AI model providers, real-time video and audio
        transport providers, transactional email providers, payment processors,
        product analytics providers, error monitoring providers, and tag
        management providers.
      </p>
      <p>
        All sub-processors are bound by data processing agreements and, where
        required, Standard Contractual Clauses for transfers outside the EEA.
        The full named list is published at{' '}
        <a href="/legal/subprocessors">
          {LEGAL_CONFIG.websiteUrl}/legal/subprocessors
        </a>
        .
      </p>

      <h2>8. Data retention</h2>
      <table>
        <thead>
          <tr>
            <th>Data category</th>
            <th>Retention period</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Interview recordings, transcripts, AI scores</td>
            <td>
              Until the recruiting organisation deletes the candidate record, or
              upon a verified erasure request.
            </td>
          </tr>
          <tr>
            <td>AI audit log entries</td>
            <td>
              Minimum {LEGAL_CONFIG.auditLogRetentionDays} days (EU AI Act Art.
              12 obligation).
            </td>
          </tr>
          <tr>
            <td>Customer account data</td>
            <td>Duration of the contract plus 12 months.</td>
          </tr>
          <tr>
            <td>Billing records</td>
            <td>7 years (Swedish Accounting Act requirement).</td>
          </tr>
        </tbody>
      </table>

      <h2>9. Your rights</h2>
      <p>
        Under the GDPR, you have the following rights regarding your personal
        data:
      </p>
      <ul>
        <li>
          <strong>Access (Art. 15):</strong> request a copy of the data we hold
          about you.
        </li>
        <li>
          <strong>Rectification (Art. 16):</strong> request correction of
          inaccurate data.
        </li>
        <li>
          <strong>Erasure (Art. 17):</strong> request deletion of your data
          where no legal obligation requires retention.
        </li>
        <li>
          <strong>Portability (Art. 20):</strong> receive your data in a
          structured, machine-readable format.
        </li>
        <li>
          <strong>Objection (Art. 21):</strong> object to processing based on
          legitimate interest.
        </li>
        <li>
          <strong>Explanation (EU AI Act Art. 86):</strong> receive an
          explanation of the AI-generated scores applied to your interview.
        </li>
      </ul>
      <p>
        To exercise any of these rights, email{' '}
        <a href={`mailto:${LEGAL_CONFIG.privacyEmail}`}>
          {LEGAL_CONFIG.privacyEmail}
        </a>
        . We will respond within 30 days. You also have the right to lodge a
        complaint with the Swedish Authority for Privacy Protection (IMY) at{' '}
        <a href="https://www.imy.se" target="_blank" rel="noreferrer">
          imy.se
        </a>
        .
      </p>

      <h2>10. Security</h2>
      <p>
        We apply industry-standard technical and organisational measures
        including encryption in transit (TLS), encryption at rest, access
        controls, and regular security reviews. Interview recordings are stored
        in private, access-controlled object storage.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this policy. Material changes will be communicated via
        email to registered customers at least 14 days before taking effect.
        Continued use of the Service after the effective date constitutes
        acceptance.
      </p>

      <h2>12. Contact</h2>
      <p>
        {LEGAL_CONFIG.companyName}
        <br />
        {LEGAL_CONFIG.companyAddress}
        <br />
        <a href={`mailto:${LEGAL_CONFIG.privacyEmail}`}>
          {LEGAL_CONFIG.privacyEmail}
        </a>
      </p>
    </article>
  )
}
